import { pick } from "lodash-es";
import { satisfies } from "semver";

import { useSelector } from "../store";
import { useGetClusterQuery } from "../store/pomeriumApi";

// if we have no version information (e.g. a cluster has never connected),
// these will be the versions we use
const defaultComponentVersions: Record<string, string> = {
  ssh: "0.1.0",
  "zero-traffic-metrics": "0.1.0",
};

type FallbackComponentVersion = {
  condition: string;
  components: Record<string, string>;
};

const fallbackComponentVersions: FallbackComponentVersion[] = [
  { condition: ">=0.30.0", components: pick(defaultComponentVersions, "ssh") },
  {
    condition: ">=0.27.0",
    components: pick(defaultComponentVersions, "zero-traffic-metrics"),
  },
];

// useGetCurrentClusterComponentVersions returns an array of component versions
// for a set of cluster replicas.
function useGetCurrentClusterComponentVersions(component: string): string[] {
  const organizationId = useSelector((state) => state.current.organizationId);
  const clusterId = useSelector((state) => state.current.clusterId);
  const { data: cluster } = useGetClusterQuery(
    {
      organizationId,
      clusterId,
    },
    { skip: !organizationId || !clusterId },
  );

  const versions = cluster?.componentVersions?.[component];
  if (versions) {
    return versions;
  }

  // if no component versions were set, we may be using an older version of Pomerium
  // which only published cluster versions
  if (cluster?.minReplicaVersion) {
    for (const { condition, components } of fallbackComponentVersions) {
      if (
        satisfies(cluster.minReplicaVersion, condition) &&
        components[component]
      ) {
        return [components[component]];
      }
    }
    return [];
  }

  return component in defaultComponentVersions
    ? [defaultComponentVersions[component]]
    : [];
}
export default useGetCurrentClusterComponentVersions;
